/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

import React from 'react'
import { Portal } from 'react-portal'
import { useCallback, useEffect, useModalOpened, useUniqueId } from 'sb-hooks'
import { constants, TrapFocus } from 'common/helpers'
import cx from 'classnames'

import Overlay from 'components/_new/layout/Overlay/Overlay'
import CloseIconButton from 'components/_new/controls/CloseIconButton/CloseIconButton'

import messages from './messages'
import s from './Modal.scss'


export type ModalBaseProps = {
  className?: string
  overlayClosable?: boolean
  htmlRole?: string
  ariaLabelledBy?: string
  ariaDescribedby?: string
  dataTestId?: string
  onClose: () => void
}

const ModalBase: React.FunctionComponent<ModalBaseProps> = (props) => {
  const {
    children, className,
    overlayClosable = true,
    htmlRole = 'dialog', ariaLabelledBy, ariaDescribedby,
    dataTestId, onClose,
  } = props

  const uniqueId    = useUniqueId()
  const trapFocusId = `trapfocus_${uniqueId}`

  useModalOpened()

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const trapFocusElement = document.getElementById(trapFocusId)

      // could be "null" if modal opened in iframe
      if (trapFocusElement) {
        const trapFocus = new TrapFocus(trapFocusElement, { withInitialFocus: false })

        trapFocusElement.focus()
        trapFocus.mount()

        return () => {
          trapFocus.unmount()
        }
      }
    }
  }, [ trapFocusId ])

  const handleOverlayClick = useCallback(() => {
    if (overlayClosable) {
      if (typeof onClose === 'function') {
        onClose()
      }
    }
  }, [ overlayClosable, onClose ])

  const handleCloseButtonClick = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose()
    }
  }, [ onClose ])

  const handleModalClick = useCallback((event) => {
    event.stopPropagation()
  }, [])

  return (
    <Portal>
      <Overlay className={s.modalContainer} onClick={handleOverlayClick}>
        <div
          id={trapFocusId}
          className={cx(s.modal, className)}
          aria-modal
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedby}
          role={htmlRole}
          tabIndex={0}
          data-testid={dataTestId}
          onClick={handleModalClick}
        >
          <CloseIconButton
            className={s.closeIcon}
            ariaLabel={messages.close}
            dataTestId={constants.testIds.modal.closeIcon}
            onClick={handleCloseButtonClick}
          />
          {children}
        </div>
      </Overlay>
    </Portal>
  )
}


export default ModalBase
